import { openAuthorizeUrl } from "@/utils/linkedaccounts/linkedAccounts";
import type { SocialQuestData } from "@/utils/linkedaccounts/types";
import { X_MEDIA_UPLOAD_STORAGE_KEY } from "@/utils/linkedaccounts/x";
import { getQuestCompletionStatus } from "@/utils/quest/questCompletionUtils";
import {
  Body,
  Box,
  ButtCon,
  Button,
  type DeeplyNestedSx,
  FormControl,
  Heading,
  Icon,
  Modal,
  type OrderedSx,
  Stack,
  TextArea,
} from "@biom3/react";
import type { Content } from "@prismicio/client";
import { useCallback, useMemo, useRef, useState } from "react";
import { fileToBase64 } from "../../LinkedAccountLogin";
import { XModalContainer, XModelContentSx } from "./XModalContainer";

type XPostModalProps = {
  visible: boolean;
  onClose: () => void;
  questContent: Content.XQuestDataDocument;
  questData: SocialQuestData;
  questCompleted: boolean;
  latestCompletion?: string;
  refreshPeriodHours?: number;
  validFrom?: string;
};

export function XPostModal({
  visible,
  onClose,
  questContent,
  questData,
  questCompleted,
  latestCompletion,
  refreshPeriodHours,
  validFrom,
}: XPostModalProps) {
  const [waitingForCompletion, setWaitingForCompletion] = useState(false);

  const { completedForPeriod: questAlreadyCompleted, recurrenceString } =
    useMemo(() => {
      return getQuestCompletionStatus({
        completed: questCompleted,
        latestCompletion,
        refreshPeriodHours,
        validFrom,
      });
    }, [questCompleted, latestCompletion, refreshPeriodHours, validFrom]);

  const handlePostOnX = async (
    text: string,
    required: boolean,
    selectedImage?: File,
  ) => {
    // Handle image upload if required and provided
    if (selectedImage && required) {
      const base64Image = await fileToBase64(selectedImage);
      const imageData = {
        base64: base64Image,
        filename: selectedImage.name,
        type: selectedImage.type,
      };
      const key = `${X_MEDIA_UPLOAD_STORAGE_KEY}_${questData.ruleId}`;
      localStorage.setItem(key, JSON.stringify(imageData));
    }

    const tweetContent = `${text} ${questContent.data.post_handle}`;

    localStorage.removeItem(questData.ruleId);
    openAuthorizeUrl(
      questData.ruleId,
      questData.key,
      questData.questLink,
      questData.gameName,
      questData.gameId,
      false,
      tweetContent,
    );
    setWaitingForCompletion(true);
  };

  const modalSX: DeeplyNestedSx | OrderedSx = { ...XModelContentSx };
  if (questContent.data.image_required && !questAlreadyCompleted) {
    modalSX.height = "auto";
    modalSX.maxHeight = "700px";
  }

  return (
    <Modal
      visible={visible}
      onCloseModal={onClose}
      showBgOverlay={false}
      outsideClicksClose={false}
    >
      <Modal.Content sx={modalSX}>
        <XModalContainer
          waitingForCompletion={waitingForCompletion}
          onClose={onClose}
          ruleId={questData.ruleId}
        >
          <PostContent
            questContent={questContent}
            questAlreadyCompleted={questAlreadyCompleted}
            recurrenceString={recurrenceString}
            onPost={handlePostOnX}
            onClose={onClose}
          />
        </XModalContainer>
      </Modal.Content>
    </Modal>
  );
}

function PostContent({
  questContent,
  questAlreadyCompleted,
  recurrenceString,
  onPost,
  onClose,
}: {
  questContent: Content.XQuestDataDocument;
  questAlreadyCompleted: boolean;
  recurrenceString: string;
  onPost: (text: string, required: boolean, selectedImage?: File) => void;
  onClose: () => void;
}) {
  const [tweetCustomText, setTweetCustomText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tweetRequiresImage = questContent.data.image_required;

  const handleImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        setImageError(
          "Please select a valid image file (JPEG, PNG, WebP, or GIF).",
        );
        return;
      }

      // Validate file size (max 5MB for X)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setImageError("Image file size must be less than 5MB.");
        return;
      }

      setSelectedImage(file);
      setImageError(null);
    },
    [],
  );

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImageError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  return (
    <Stack
      sx={{
        height: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "base.spacing.x1",
      }}
    >
      <Heading
        size="medium"
        weight="bold"
        sx={{ mx: "base.spacing.x12", textAlign: "center" }}
      >
        Posting at {questContent.data.post_handle}
      </Heading>

      {questAlreadyCompleted ? (
        <Body
          sx={{
            textAlign: "center",
            color: "base.color.text.body.secondary",
            px: "base.spacing.x4",
          }}
        >
          You've already done this for {recurrenceString}! Check back later to
          complete it again.
        </Body>
      ) : (
        <>
          <Body
            sx={{
              px: "base.spacing.x10",
              color: "base.color.text.body.secondary",
              textAlign: "center",
              fontSize: "base.text.body.small.regular.fontSize",
            }}
          >
            {questContent.data.post_prompt}
          </Body>
          <FormControl>
            <TextArea
              sizeVariant={"medium"}
              validationStatus={error ? "error" : undefined}
              value={tweetCustomText}
              hideClearValueButton={false}
              onClearValue={() => setTweetCustomText("")}
              onChange={(event) => {
                setTweetCustomText(event.target.value);
                if (error) setError(null);
              }}
              sx={{
                minWidth: "296px",
                mt: "base.spacing.x2",
                color: "base.color.text.body.secondary",
                position: "relative",

                /* render the prefix text before the textarea */
                "&::before": {
                  content: `"${questContent.data.post_handle}"`,
                  position: "absolute",
                  fontFamily: "base.font.family.body.primary",
                  whiteSpace: "pre-wrap",
                  maxWidth: "250px",
                },

                "& textarea": {
                  resize: "none",
                  mt: "base.spacing.x6",
                  color: "base.color.text.body.secondary",
                  lineHeight: "base.spacing.x5",
                },
                "& button": {
                  mt: "base.spacing.x6",
                },
              }}
            />
            {error && (
              <FormControl.Validation validationStatus="error">
                {error}
              </FormControl.Validation>
            )}
          </FormControl>
        </>
      )}
      {tweetRequiresImage && !questAlreadyCompleted && (
        <Stack
          sx={{
            width: "100%",
            alignItems: "center",
            gap: "base.spacing.x3",
            px: "base.spacing.x4",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleImageSelect}
            style={{ display: "none" }}
          />
          {!selectedImage && (
            <Button
              variant="tertiary"
              size="medium"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: "296px",
                border: "1px dashed",
                borderColor: "base.color.translucent.standard.400",
                color: "base.color.text.body.secondary",
                "&:hover": {
                  borderColor: "base.color.accent.2",
                  color: "base.color.accent.2",
                },
              }}
            >
              <Icon icon="Add" sx={{ mr: "base.spacing.x2" }} />
              Add Image
            </Button>
          )}
          {selectedImage && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "base.spacing.x3",
                width: "296px",
                padding: "base.spacing.x3",
                borderRadius: "base.borderRadius.x4",
                border: "1px solid",
                borderColor: "base.color.translucent.standard.400",
                backgroundColor: "base.color.translucent.standard.100",
              }}
            >
              <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "base.borderRadius.x2",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="Selected"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: "0", overflow: "hidden" }}>
                <Body
                  size="small"
                  sx={{
                    color: "base.color.text.body.primary",
                    fontWeight: "base.text.body.small.bold.fontWeight",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  {selectedImage.name}
                </Body>
                <Body
                  size="xSmall"
                  sx={{
                    color: "base.color.text.body.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                >
                  Image attached
                </Body>
              </Box>
              <ButtCon
                icon="Close"
                variant="tertiary"
                onClick={handleRemoveImage}
                sx={{
                  flexShrink: 0,
                  width: "24px",
                  height: "24px",
                  ml: "base.spacing.x2",
                  "&:hover": {
                    backgroundColor: "base.color.translucent.standard.300",
                  },
                }}
              />
            </Box>
          )}
          {imageError ? (
            <Body
              size="small"
              sx={{
                color: "base.color.status.fatal.bright",
                textAlign: "center",
                maxWidth: "296px",
              }}
            >
              {imageError}
            </Body>
          ) : (
            !selectedImage && (
              <Body
                size="xSmall"
                sx={{
                  color: "base.color.text.body.secondary",
                  textAlign: "center",
                  maxWidth: "296px",
                }}
              >
                Supported formats: JPEG, PNG, WebP, GIF (max 5MB)
              </Body>
            )
          )}
        </Stack>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          px: "base.spacing.x4",
          pb: "base.spacing.x4",
        }}
      >
        {questAlreadyCompleted ? (
          <Button
            variant="tertiary"
            onClick={onClose}
            sx={{ width: "100%", my: "base.spacing.x1" }}
            size="large"
          >
            Go back
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              if (tweetCustomText.trim() === "") {
                setError("The post content cannot be empty.");
                return;
              }

              // Check if image is required but not attached
              if (tweetRequiresImage && !selectedImage) {
                setImageError("An image is required for this post.");
                return;
              }

              setError(null);
              setImageError(null);
              setIsUploading(true);

              try {
                onPost(
                  tweetCustomText,
                  tweetRequiresImage,
                  selectedImage || undefined,
                );
              } catch (error) {
                console.error("Failed to post:", error);
                setImageError("Failed to post. Please try again.");
              } finally {
                setIsUploading(false);
              }
            }}
            sx={{ width: "100%", my: "base.spacing.x1" }}
            size="large"
            disabled={isUploading}
          >
            {isUploading ? "Posting..." : "Post on X"}
          </Button>
        )}
      </Box>
    </Stack>
  );
}
